/**
 * 問い合わせに「足す / 外す → 決定 / パス」で答える規則（Issue #773 段階 2c。索引 #771）。純粋（状態を持たない）。
 * Web の応答タブ・iPad の画面・M5Dial・Android が同じものを使う（以前は Dial の toggle、同梱 JS の togglePicked、Web の RespondTab の 3 か所にあった）。
 *
 * sel = { picks: [index], counts: { [index]: n }, helper: [index] | null }
 *   - helper は、もう一方の席の問い合わせを手伝うときに足せる選択肢（null なら自分が答える）
 * 答え方のモードはサーバーの query.answer（#772）。answer の無い合成の問い合わせ（撮影・戻って発動）だけ min / max / value_kind / special から出す
 */
export const PASS_ORDER = ['pass', 'no', 'cancel', 'finish', 'skip'];

const options = (q) => (q && Array.isArray(q.options) ? q.options : []);
const isSpecial = (q, i) => options(q)[i]?.special != null;
const norm = (sel) => ({ picks: sel?.picks ?? [], counts: sel?.counts ?? {}, helper: sel?.helper ?? null });

/** 答え方のモード（single / multi / order / counts） */
export function answerMode(q) {
  const mode = q?.answer?.mode;
  if (mode) return mode;
  const vk = q?.value_kind;
  if (vk === 'order' || vk === 'counts') return vk;
  return q?.max === 1 ? 'single' : 'multi';
}

/** パス系の候補（チェーンしない > 発動しない > やめる > 選び終わる > 並べ替えない）の index。無ければ null */
export function passIndex(q) {
  if (q?.answer && 'pass' in q.answer) return q.answer.pass ?? null;
  for (const kind of PASS_ORDER) {
    const o = options(q).find((x) => x.special === kind);
    if (o && typeof o.index === 'number') return o.index;
  }
  return null;
}

const countsSum = (counts) => Object.values(counts).reduce((a, b) => a + (b ?? 0), 0);

/** 候補を押したときの次の選び方 → { picks, counts, status }（status は選べなかった理由。選べたら null）
 *  - 1 つ選ぶものは入れ替え（手伝うときは足す）、複数は max まで足す / 外す、キャンセルなどの special は単独
 *  - 順番（order）は押した順＝上から置く順、もう一度押すと外す。個数（counts）は押すたびに +1、そのカードの上限を超えたら 0、合計は total まで
 *  - addOnly（画像認識・パッドで指した）は外さない */
export function toggle(sel, q, index, { addOnly = false } = {}) {
  const { picks, counts, helper } = norm(sel);
  const opts = options(q);
  const same = (status = null) => ({ picks, counts, status });
  if (!opts[index]) return same();
  const mode = answerMode(q);
  if ((mode === 'order' || mode === 'counts') && !isSpecial(q, index)) {
    const kept = picks.filter((i) => !isSpecial(q, i));
    if (mode === 'order') {
      if (kept.includes(index)) return { picks: addOnly ? kept : kept.filter((i) => i !== index), counts, status: null };
      return { picks: [...kept, index], counts, status: null };
    }
    let n = (counts[index] ?? 0) + 1;
    const cap = opts[index].max;
    if (cap != null && n > cap) n = 0;
    if (n > 0 && q.total != null && countsSum(counts) - (counts[index] ?? 0) + n > q.total) return { picks: kept, counts, status: `合計 ${q.total} まで` };
    const next = { ...counts, [index]: n };
    return { picks: Object.keys(next).map(Number).sort((a, b) => a - b).filter((i) => (next[i] ?? 0) > 0), counts: next, status: null };
  }
  if (picks.includes(index)) return { picks: addOnly ? picks : picks.filter((i) => i !== index), counts, status: null };
  if (isSpecial(q, index)) return { picks: [index], counts, status: null };   // キャンセル・選び終わる などは単独
  const kept = picks.filter((i) => !isSpecial(q, i));
  const max = q.max ?? null;
  if (max === 1 && helper == null) return { picks: [index], counts, status: null };
  if (max == null || kept.length < max) return { picks: [...kept, index], counts, status: null };
  return { picks: kept, counts, status: `${max} つまで` };
}

/** 決定できるか（枚数が min〜max で重みの合計が weight_min 以上・順番は全部・個数は合計が total。キャンセルなどの special は単独で）。手伝うときは決めない */
export function ready(sel, q) {
  const { picks, counts, helper } = norm(sel);
  if (!q || options(q).length === 0 || helper != null || picks.length === 0) return false;
  if (picks.length === 1 && isSpecial(q, picks[0])) return true;
  switch (answerMode(q)) {
    case 'order': return picks.length === options(q).filter((o) => o.special == null && o.skipped == null).length;
    case 'counts': return countsSum(counts) === (q.total ?? 0);
    default: {
      const weight = picks.reduce((s, i) => s + (options(q)[i]?.weight ?? 1), 0);
      return (q.min ?? 1) <= picks.length && picks.length <= (q.max ?? options(q).length) && weight >= (q.weight_min ?? 0);
    }
  }
}

/** 選んでいる途中で決定 → { answer, status }（決定できなければ answer null と理由）。順番・個数は value、それ以外は picks */
export function answerFromPicks(sel, q) {
  const { picks, counts } = norm(sel);
  const id = q?.id;
  if (id == null) return { answer: null, status: null };
  const mode = answerMode(q);
  const specialOnly = picks.length === 1 && isSpecial(q, picks[0]);
  if ((mode === 'order' || mode === 'counts') && !specialOnly) {
    if (!ready(sel, q)) return { answer: null, status: mode === 'order' ? '全部のカードの順を決めてから決定' : `合計 ${q.total ?? 0} にしてから決定` };
    const value = mode === 'order' ? picks : options(q).filter((o) => o.special == null && o.skipped == null).map((o) => counts[o.index] ?? 0);
    return { answer: { id, value }, status: null };
  }
  if (!ready(sel, q)) return { answer: null, status: `${q.min ?? 1}〜${q.max ?? options(q).length} 選んでから決定` };
  return { answer: { id, picks: [...picks] }, status: null };
}

/** パス系の候補で答える → { answer, status }。手伝うときはしない */
export function passAnswer(sel, q) {
  const { helper } = norm(sel);
  const id = q?.id;
  if (id == null || helper != null) return { answer: null, status: '決めるのは相手の席' };
  const i = passIndex(q);
  if (i == null) return { answer: null, status: 'パスできる問い合わせではない' };
  return { answer: { id, picks: [i] }, status: null };
}
